import React from 'react';
import Button from '@material-ui/core/Button';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link as RouterLink, match } from 'react-router-dom';
// Components
import App from '../../../components/app';
import ErrorMessage from '../../../components/error-message';
import EnhancedListItem from '../../../components/listItem';
import { FormatType, ListCell, ActionFunction } from '../../../local-types';
import MyLink from '../../../components/mylink';
import { LCAlert } from '../../../components/alert';
import { SentMessages } from '../../../components/sent-messages';
// Modules
import { Manager, TParams } from '../../../redux/reducers/api-types';
import { ErrorPayload, selectors as errorSelectors } from '../../../redux/reducers/errors';
import { actions as partnerActions, selectors as partnerSelectors } from '../../../redux/reducers/partners';
// Ressources
import UserUtils, { AccountRight, RightRank } from '../../../utils/userUtils';
import { paths, createPath } from '../../paths';

interface PartnerManagerViewProps extends WithTranslation {
    match: match<TParams>;
    getPartnerManagerError: ErrorPayload;
    resetManagerPasswordError: ErrorPayload;
    managers: Manager[];
    getManager: (id: string, idm: string) => void;
    resetManagerPassword: (id: string, callback: React.EffectCallback) => void;
}

class PartnerManagerViewPage extends React.Component<PartnerManagerViewProps> {
    isPartnerSettings: boolean = UserUtils.checkRights({
        name: AccountRight.PARTNER_SETTINGS,
        mode: RightRank.W,
    });
    alertRef: any;

    componentDidMount() {
        const { match, managers, getManager } = this.props;
        const { id, idm } = match.params;

        if (!managers.find((m) => String(m.id) === idm)) {
            getManager(id, idm);
        }
    }

    //========================================//
    //=============== Builders ===============//
    //========================================//

    generateListItems = (payload: Manager): ListCell[] => {
        const { t } = this.props;
        const listItem: ListCell[] = [
            { title: t('common:form.id'), data: payload.id, readOnly: true },
            { title: t('common:form.firstname'), data: payload.firstname },
            { title: t('common:form.lastname'), data: payload.lastname },
            { title: t('common:form.email'), data: payload.email },
            { title: t('common:form.phone_number'), data: payload.phone_number, type: FormatType.Phone },
            {
                title: t('common:suspended'),
                data: payload.banned,
                type: FormatType.Boolean,
            },
            {
                title: t('common:form.partner'),
                readOnly: true,
                data: (
                    <MyLink color="secondary" to={`${paths.partners}/${payload.partner.id}`}>
                        {payload.partner.name}
                    </MyLink>
                ),
            },
        ];
        return listItem;
    };

    renderActions = (): ActionFunction[] => {
        const { t, match } = this.props;
        const { idm } = match.params;

        const actions: ActionFunction[] = [];

        if (this.isPartnerSettings) {
            actions.push({
                label: t('common:reset_password'),
                func: () =>
                    this.props.resetManagerPassword(idm, () => {
                        this.alertRef.showAlert();
                    }),
            });
        }
        return actions;
    };

    render() {
        const { t, match, managers, getPartnerManagerError } = this.props;
        const { id, idm } = match.params;
        const payload = managers.find((m) => String(m.id) === idm);

        return (
            <App
                header={{
                    icon: 'default',
                    title: payload?.fullname,
                    subtitle: payload && `ID: ${payload.id}`,
                    bubble: {
                        visible: payload?.banned,
                        text: t('common:suspended'),
                    },
                    actions: this.renderActions(),
                }}
            >
                <ErrorMessage payload={getPartnerManagerError} />
                <ErrorMessage payload={this.props.resetManagerPasswordError} />
                <LCAlert
                    onRef={(ref) => (this.alertRef = ref)}
                    details={t('login:forgot_password_sent')}
                    type="success"
                />
                {payload && (
                    <>
                        <br />
                        <EnhancedListItem
                            datas={this.generateListItems(payload)}
                            title={t('common:form.general_infos')}
                            cardActions={
                                this.isPartnerSettings && (
                                    <Button
                                        color="secondary"
                                        variant="contained"
                                        component={RouterLink}
                                        to={createPath(paths.partnersManagersEdit, { id, idm })}
                                    >
                                        {t('common:edit')}
                                    </Button>
                                )
                            }
                        />
                        <SentMessages uuid={payload.uuid} />
                    </>
                )}
            </App>
        );
    }
}

const PartnerManagerViewPageWithTranslation = withTranslation(['partner', 'common'])(PartnerManagerViewPage);

const mapStateToProps = (state, props) => ({
    managers: partnerSelectors.partnersManagersWithPartner(state, props?.match?.params?.id),
    getPartnerManagerError: errorSelectors.getPartnerManagerError(state),
    resetManagerPasswordError: errorSelectors.resetManagerPasswordError(state),
});

const mapDispatchToProps = {
    getManager: partnerActions.getManager,
    resetManagerPassword: partnerActions.resetManagerPassword,
};

export default connect(mapStateToProps, mapDispatchToProps)(PartnerManagerViewPageWithTranslation);
